import styled from 'styled-components';
import { DatePicker, Input } from 'antd';
import { CustomDropdown } from './CustomDropdown';
import { primaryFont, wMLight, maxTablet, greyColor, textBlack, primaryColor } from '../../../../Utils/utilsStyle';
import { useState } from 'react';
import dayjs from 'dayjs';

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  .ant-custom-popover {
    position: relative;
    width: 100%;
    height: 36px;
    &:hover {
      border: 1px solid ${primaryColor};
    }
    &:focus {
      border: 1px solid ${primaryColor};
    }
    .arrorwUp-icon path {
      fill: ${greyColor};
    }
    .calendar-icon {
      width: 16px;
      height: 16px;
    }
  }
  .ant-popover {
    width: 100% !important;
    .ant-popover-inner {
      padding: 0px;
      .ant-popover-inner-content {
        max-width: 100%;
      }
    }
  }
`;

const Label = styled.label<{ $required?: boolean }>`
  color: #0D0D0D;
  font-family: ${primaryFont};
  font-size: 14px;
  font-weight: ${wMLight};
  letter-spacing: -0.239px;
  display: flex;
  align-items: center;
  min-width: 85px;
  @media all and (max-width: ${maxTablet}) {
    font-size: 12px;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 8px;
  border: 1px solid #DADADA;
  background: #FFF;
  width: 100%;
  height: 36px;
  padding: 8px 12px;
  font-family: ${primaryFont};
  font-size: 14px;
  color: ${textBlack};
  cursor: pointer;
  @media all and (max-width: ${maxTablet}) {
    font-size: 12px;
  }
  &:hover, &:focus {
    border-color: #00A651;
    box-shadow: none;
  }
`;

const StyledDatePicker = styled(DatePicker)`
    width: 100%;
    height: 36px;
    border-radius: 6px;
    border-color: #DADADA;
    box-shadow: none;
    cursor: pointer;
    &:hover, &:focus-within {
      border-color: ${primaryColor};
      box-shadow: none;
    }
    svg {
      fill: #7E7E7E;
    }
    input {
      cursor: pointer;
    }
    input::placeholder {
      color: #00000040;
      font-family: ${primaryFont};
      font-size: 14px;
      letter-spacing: -0.4px;
    }
`;

interface FormFieldProps {
  label: string;
  required?: boolean;
  type?: 'text' | 'dropdown' | 'date';
  value?: string | number;
  onChange?: (value: any) => void;
  placeholder?: string;
  options?: { id: number; label: string; image?: string; subtitle?: string }[];
  hasSearch?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  isLoading?: boolean;
  maxHeight?: string;
  searchPlaceholder?: string;
  onSearchSubmit?: (term: string) => void;
  useRemoteSearch?: boolean;
  variant?: 'text' | 'iconText' | 'iconTextDropdown' | 'customIconTextDropdown';
  isCardOption?: boolean;
  onDropdownOpen?: () => void;
  initialSearchTerm?: string;
  sup?: string;
  isSupText?: boolean;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  type = 'text',
  value,
  onChange,
  placeholder,
  options = [],
  hasSearch = false,
  hasMore = false,
  loadMore = () => { },
  isLoading = false,
  maxHeight = "",
  searchPlaceholder = "Search...",
  onSearchSubmit,
  useRemoteSearch,
  variant,
  isCardOption,
  onDropdownOpen,
  initialSearchTerm,
  sup,
  isSupText = false,
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const handleDatePickerChange = (date: unknown, dateString: string | string[]) => {
    onChange?.(Array.isArray(dateString) ? dateString[0] : dateString);
    setIsDatePickerOpen(false);
  };

  const handleDatePickerOpenChange = (open: boolean) => {
    setIsDatePickerOpen(open);
  };

  // Convert string value to Dayjs for DatePicker
  const dateValue = value && type === 'date' ? dayjs(value as string) : null;

  return (
    <FieldWrapper className="advance-search-field-wrapper">
      <Label $required={required}>{label}{isSupText && <sup>[{sup}]</sup>}</Label>
      {type === 'text' && (
        <StyledInput
          value={value as string}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
        />
      )}
      {type === 'dropdown' && (
        <CustomDropdown
          options={options}
          selectedId={value as number}
          onSelect={(id) => onChange?.(id)}
          placeholder={placeholder || `Select ${label.toLowerCase()}`}
          hasSearch={hasSearch}
          hasMore={hasMore}
          loadMore={loadMore}
          isLoading={isLoading}
          maxHeight={maxHeight}
          searchPlaceholder={searchPlaceholder}
          onSearchSubmit={onSearchSubmit}
          useRemoteSearch={useRemoteSearch}
          variant={variant}
          isCardOption={isCardOption}
          onDropdownOpen={onDropdownOpen}
          initialSearchTerm={initialSearchTerm}
        />
      )}
      {type === 'date' && (
        <StyledDatePicker
          placeholder={placeholder}
          onChange={handleDatePickerChange}
          open={isDatePickerOpen}
          onOpenChange={handleDatePickerOpenChange}
          value={dateValue}
          getPopupContainer={(trigger) => trigger.parentElement || document.body}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </FieldWrapper>
  );
};